import styled from 'styled-components/macro';

import { large } from 'styles/breakpoints';
import { container } from 'styles/layout';
import { heading, headingCalendar, input } from 'styles/typography';
import { IWithThemeProps } from 'typings';

interface ICalendarNavButtonProps {
  direction: 'left' | 'right';
}

interface ICalendarRowProps {
  align?: 'left' | 'right';
}

export const Header = styled.h1`
  ${heading}

  ${({ theme }: IWithThemeProps) => `
    color: ${theme.topForegroundPrimary};
  `}
`;

export const Calendar = styled.div`
  ${container}

  margin: 0 auto 32px;
`;

export const CalendarNav = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
`;

export const CalendarHeading = styled.h2`
  ${headingCalendar}

  margin: 0 10px;
  min-width: 120px;
  padding: 8px 0;

  ${({ theme }: IWithThemeProps) => `
    color: ${theme.topForegroundPrimary};
  `}
`;

export const CalendarNavButton = styled.button<ICalendarNavButtonProps>`
  background: none;
  border: 0;
  cursor: pointer;
  outline: none;
  padding: 8px 13px;

  ${({ theme }: IWithThemeProps) => `
    [data-svg-class='CalendarChevron-stroke-opacity'] {
      transition: stroke-opacity ${theme.transitionDurationPrimary} ease;
    }
  `}

  &:hover,
  &:focus {
    [data-svg-class='CalendarChevron-stroke-opacity'] {
      stroke-opacity: 1;
    }
  }

  &:disabled {
    visibility: hidden;
  }

  ${({ direction }) => {
    return direction === 'left' ? 'transform: scale(-1, 1)' : '';
  }}
`;

export const CalendarDates = styled.div`
  position: relative;
`;

export const CalendarRow = styled.div<ICalendarRowProps>`
  align-items: center;
  display: flex;

  ${({ align = 'left' }) => `
    justify-content: ${align === 'left' ? 'flex-start' : 'flex-end'};
  `}

  ${({ theme }: IWithThemeProps) => `
    color: ${theme.topForegroundPrimary};
  `}
`;

export const DayHeading = styled.span`
  ${input}

  display: block;
  font-weight: 600;
  line-height: 1;
  margin: 0;
  width: 14.285%;
`;

export const DayBlock = styled.div`
  align-items: center;
  display: flex;
  height: 49px;
  justify-content: center;
  line-height: 48px;
  width: 14.285%;
`;

interface IDayButtonProps {
  isHidden: boolean;
  isSelected: boolean;
  isToday: boolean;
}

export const DayButton = styled.button<IDayButtonProps>`
  ${input}

  background: none;
  border-radius: 100%;
  cursor: pointer;
  display: block;
  height: 42px;
  line-height: 41px;
  outline: none;
  padding: 0;
  transition-property: background-color, border-color, box-shadow, color,
    opacity;
  transition-timing-function: ease;
  width: 42px;

  ${({ theme }: IWithThemeProps) => `
    border: 1px solid ${theme.topForegroundMuted};
    color: ${theme.topForegroundPrimary};
    transition-duration: ${theme.transitionDurationPrimary};

    &:disabled {
      border-color: transparent;
      color: ${theme.topForegroundMuted};
      cursor: default;
    }

    &:focus {
      box-shadow: 0 0 10px ${theme.calendarDaySelectedBackgroundColor};
    }
  `}

  ${large`
    height: 45px;
    line-height: 45px;
    width: 45px;
  `}

  ${({ isSelected, theme }) => {
    if (isSelected) {
      return `
        background-color: ${theme.calendarDaySelectedBackgroundColor};
        border-color: ${theme.topForegroundPrimary};
        color: ${theme.calendarDaySelectedColor};
      `;
    }

    return '';
  }}

  ${({ isToday, theme }) => {
    if (isToday) {
      return `
        &:after {
          background: ${theme.topForegroundMuted};
          border-radius: 3.5px;
          content: '';
          display: block;
          height: 7px;
          margin: auto;
          position: relative;
          top: -12px;
          width: 7px;

          ${large`
            top: -13px;
          `}
        }
      `;
    }

    return '';
  }}

  ${({ isHidden }) => `
    opacity: ${isHidden ? 0 : 1}
  `}
`;
